import type * as Phaser from 'phaser';
import type { GameSceneContext, MissionStageDefinition } from '../../sdk/mission';
import type { StageId } from '../../shared/types/ids';
import type { MissionRuntimeEvent } from '../missions';

/**
 * Runs the module's own scene for whichever stage the mission is on (module spec §9, recon §5
 * option (a)).
 *
 * The SDK's integration point is `MissionStageDefinition.sceneFactory`, and until this existed
 * nothing ever called it: the shell hardcoded one platform preview scene and fed it a narrowed
 * projection of the round over the app bus. So a game could not draw its own mechanics, and the
 * contract every module implements was dead code.
 *
 * One stage is on screen at a time. `STAGE_ENTERED` is the swap signal, and the mission runtime
 * emits it for narrative stages too, so the intro and victory beats are scenes like any other.
 *
 * **Failure is readable, never blank.** A factory that throws, or returns something Phaser will
 * not accept, gets the platform's fallback scene for that stage and a reported error — the
 * mission keeps running underneath, because a stage that cannot draw is not a reason to end a
 * child's mission.
 */

export interface StageSceneDirectorOptions {
  game: Pick<Phaser.Game, 'scene'>;
  /** The mission's own stage list, so the director can never drift from what the runtime walks. */
  stages: readonly MissionStageDefinition[];
  /** Builds the context for a stage. Kept as a callback so the director owns no game state. */
  contextFor(stage: MissionStageDefinition): GameSceneContext;
  /** Builds the readable stand-in for a stage whose own scene could not be created. */
  fallbackFor(stage: MissionStageDefinition, cause: unknown, sceneKey: string): Phaser.Scene;
  /** Reported to the shell; the mission is not torn down. */
  onError?(message: string): void;
  /**
   * Called once a stage's scene is on screen. The shell uses it to keep the platform's chrome
   * scene — and with it the overlay layer every module scene was handed — above the mechanic.
   */
  afterStart?(sceneKey: string): void;
}

export interface StageSceneDirector {
  /** Feed every runtime event; the director acts on `STAGE_ENTERED` only. */
  handle(event: MissionRuntimeEvent): void;
  /** The scene key currently on screen, or null before the first stage. */
  currentSceneKey(): string | null;
  dispose(): void;
}

function sceneKeyFor(stageId: StageId): string {
  return `thrive-stage-${stageId}`;
}

export function createStageSceneDirector(options: StageSceneDirectorOptions): StageSceneDirector {
  let currentKey: string | null = null;
  let disposed = false;

  const removeCurrent = (): void => {
    if (currentKey === null) return;
    const key = currentKey;
    currentKey = null;
    try {
      options.game.scene.remove(key);
    } catch {
      // A scene manager already torn down by a host destroy has nothing left to remove.
    }
  };

  const start = (stage: MissionStageDefinition): void => {
    const key = sceneKeyFor(stage.id);

    let scene: Phaser.Scene;
    try {
      scene = stage.sceneFactory.create(options.contextFor(stage));
    } catch (cause) {
      options.onError?.(
        `The "${stage.label ?? stage.id}" scene could not start: ${describe(cause)}`,
      );
      scene = options.fallbackFor(stage, cause, key);
    }

    try {
      // `true` starts it immediately; Phaser boots it on the next tick, which is exactly why the
      // event relay replays the situation a late scene is arriving into.
      options.game.scene.add(key, scene, true);
      currentKey = key;
      options.afterStart?.(key);
    } catch (cause) {
      options.onError?.(
        `The "${stage.label ?? stage.id}" scene could not start: ${describe(cause)}`,
      );
      currentKey = null;
    }
  };

  return {
    handle(event: MissionRuntimeEvent): void {
      if (disposed || event.type !== 'STAGE_ENTERED') return;

      const stage = options.stages.find((candidate) => candidate.id === event.stageId);
      if (stage === undefined) return;

      removeCurrent();
      start(stage);
    },

    currentSceneKey: () => currentKey,

    dispose(): void {
      if (disposed) return;
      disposed = true;
      removeCurrent();
    },
  };
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
